import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { AnimatePresence, motion } from 'framer-motion'
import FocusLock from 'react-focus-lock'
import { CloseIcon } from '@/components/icons/baba-icons'
import { detectGatedPlatform, type GatedPlatform } from '@/lib/mobileGate'
import { getStoreUrl, RATINGS } from '@/lib/storeLinks'
import { trackAppStoreClick } from '@/lib/analytics'

const SHEET_SEEN_KEY = 'baba.mobile_sheet_seen'
const SHEET_DELAY_MS = 1500

// Apple / Google Play glyph paths — same paths as src/components/StoreBadges.tsx.
function AppleGlyph({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="currentColor" role="img" aria-label="Apple">
      <path d="M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.546 9.103 1.519 12.09 1.013 1.454 2.208 3.09 3.792 3.039 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09zM15.53 3.83c.843-1.012 1.4-2.427 1.245-3.83-1.207.052-2.662.805-3.532 1.818-.78.896-1.454 2.338-1.273 3.714 1.338.104 2.715-.688 3.559-1.701" />
    </svg>
  )
}

function PlayGlyph({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="currentColor" role="img" aria-label="Google Play">
      <path d="M22.018 13.298l-3.919 2.218-3.515-3.493 3.543-3.521 3.891 2.202a1.49 1.49 0 0 1 0 2.594zM1.337.924a1.486 1.486 0 0 0-.112.568v21.017c0 .217.045.419.124.6l11.155-11.087L1.337.924zm12.207 10.065l3.258-3.238L3.45.195a1.466 1.466 0 0 0-.946-.179l11.04 10.973zm0 2.067l-11 10.933c.298.036.612-.016.906-.183l13.324-7.54-3.23-3.21z" />
    </svg>
  )
}

function readLocal(key: string): string | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage.getItem(key)
  } catch {
    return null
  }
}

function writeLocal(key: string, value: string): void {
  try {
    if (typeof localStorage !== 'undefined') localStorage.setItem(key, value)
  } catch {
    // Best-effort — worst case the banner/sheet reappears next visit.
  }
}

function isMobileViewport(): boolean {
  return typeof window !== 'undefined' && window.innerWidth < 768
}

/** Routes that already assume a phone-native context — never interrupt them with the sheet. */
function isSheetExemptRoute(pathname: string): boolean {
  // Share views only: exactly "/t" (+ query) or "/t/…" — NOT "/translate".
  return pathname === '/t' || pathname.startsWith('/t/') || pathname.startsWith('/chat/x/share')
}

function useIsMobilePromoEligible(): { eligible: boolean; platform: GatedPlatform | null } {
  const [state, setState] = useState<{ eligible: boolean; platform: GatedPlatform | null }>({
    eligible: false,
    platform: null,
  })

  useEffect(() => {
    const evaluate = () => {
      try {
        const platform = detectGatedPlatform(navigator.userAgent)
        setState({ eligible: Boolean(platform) && isMobileViewport(), platform })
      } catch {
        setState({ eligible: false, platform: null })
      }
    }
    evaluate()
    window.addEventListener('resize', evaluate)
    return () => window.removeEventListener('resize', evaluate)
  }, [])

  return state
}

export function SmartBanner({ platform, onDismiss }: { platform: GatedPlatform; onDismiss: () => void }) {
  const label = platform === 'ios' ? 'App Store' : 'Google Play'
  return (
    <div
      className="flex items-center gap-2.5 shrink-0 pl-3 pr-2.5"
      style={{ height: 50, background: 'var(--card)', borderBottom: '1px solid var(--line)' }}
    >
      <img src="/baba-app-icon.png" alt="" className="h-[34px] w-[34px] shrink-0 object-cover" style={{ borderRadius: '8px' }} />
      <div className="flex-1 min-w-0">
        <p className="text-[13px] font-semibold leading-tight truncate" style={{ color: 'var(--ink)' }}>
          baba Is Better in the App
        </p>
        <p className="text-[11.5px] leading-tight truncate" style={{ color: 'var(--muted-2)' }}>
          Voice, camera, and offline voice. Free.
        </p>
      </div>
      <a
        href={getStoreUrl(platform, 'mobile_banner')}
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => trackAppStoreClick(platform, 'mobile_banner')}
        className="shrink-0 flex items-center justify-center px-3 rounded-md text-[13px] font-semibold"
        style={{ height: 30, background: 'var(--accent)', border: '1px solid var(--accent-deep)', color: 'var(--cta-fg)' }}
      >
        Get
      </a>
      <button
        type="button"
        aria-label="Dismiss"
        onClick={onDismiss}
        className="shrink-0 flex items-center justify-center rounded-md transition-colors"
        style={{ height: 28, width: 28, color: 'var(--muted-2)' }}
      >
        <CloseIcon size={11} />
      </button>
    </div>
  )
}

function DownloadSheet({ platform, onClose }: { platform: GatedPlatform; onClose: () => void }) {
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', handler)
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', handler)
      document.body.style.overflow = prevOverflow
    }
  }, [onClose])

  if (typeof document === 'undefined') return null

  const rating = RATINGS[platform]
  const label = platform === 'ios' ? 'App Store' : 'Google Play'
  const sub = platform === 'ios' ? 'Download on the' : 'Get it on'
  const storeUrl = getStoreUrl(platform, 'mobile_sheet')

  return createPortal(
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.16 }}
        className="fixed inset-0 z-[200] flex items-end justify-center backdrop-blur-sm"
        style={{ background: 'rgba(0,0,0,0.45)' }}
        onClick={onClose}
      >
        <FocusLock returnFocus autoFocus>
          <motion.div
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ duration: 0.22, ease: [0.25, 0.1, 0.25, 1] }}
            className="w-full max-w-[480px] flex flex-col gap-3.5"
            style={{
              background: 'var(--card)',
              borderRadius: '20px 20px 0 0',
              padding: '14px 20px 34px',
              paddingBottom: 'calc(34px + env(safe-area-inset-bottom))',
            }}
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="mobile-app-sheet-title"
          >
            <div className="mx-auto" style={{ width: 36, height: 4, borderRadius: 2, background: 'var(--line)' }} />

            <div className="flex items-center gap-3.5">
              <img src="/baba-app-icon.png" alt="" className="shrink-0 object-cover" style={{ width: 56, height: 56, borderRadius: 14 }} />
              <div>
                <h2 id="mobile-app-sheet-title" className="text-[19px] font-semibold tracking-tight" style={{ color: 'var(--ink)' }}>
                  Download Our Mobile App
                </h2>
                <p className="text-[11px] mt-0.5 font-mono" style={{ color: 'var(--muted-2)' }}>
                  ★ {rating.score} · {rating.count.toLocaleString()} ratings · free
                </p>
              </div>
            </div>

            <p className="text-[14px] leading-relaxed" style={{ color: 'var(--muted)' }}>
              Point the camera at a sign, talk instead of type, and keep your saved phrases offline. Your Pro plan
              carries over. Sign in with the same email.
            </p>

            <a
              href={storeUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackAppStoreClick(platform, 'mobile_sheet')}
              className="flex items-center justify-center gap-2.5 w-full rounded-md font-semibold"
              style={{ height: 52, background: 'var(--ink)', color: 'var(--cream)' }}
            >
              {platform === 'ios' ? <AppleGlyph className="h-[18px] w-[18px] shrink-0" /> : <PlayGlyph className="h-[18px] w-[18px] shrink-0" />}
              <span className="flex flex-col items-start leading-tight" dir="ltr">
                <span className="text-[10.5px] font-medium opacity-75">{sub}</span>
                <span className="text-[15px] font-semibold">{label}</span>
              </span>
            </a>

            <button
              type="button"
              onClick={onClose}
              className="flex items-center justify-center w-full rounded-md text-[14px] font-medium"
              style={{ height: 44, border: '1px solid var(--line)', background: 'transparent', color: 'var(--ink)' }}
            >
              Keep Using the Web
            </button>

            <p className="text-center text-[12px]" style={{ color: 'var(--muted-2)' }}>
              Already have it?{' '}
              <a
                href={storeUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackAppStoreClick(platform, 'mobile_sheet_open')}
                className="font-medium"
                style={{ color: 'var(--accent-deep)' }}
              >
                Open in the App
              </a>
            </p>
          </motion.div>
        </FocusLock>
      </motion.div>
    </AnimatePresence>,
    document.body,
  )
}

/**
 * Mobile app promo for phone-web visitors — the "Option 2" soft gate. Only
 * renders for detected iOS/Android UAs under the md breakpoint (768px):
 *
 *  - a one-time bottom sheet, shown once per device 1.5s after the first
 *    completed translation (SHEET_SEEN_KEY), skipped on share routes
 *    (/t, /chat/x/share)
 *
 * The persistent smart banner that used to sit above every page is gone
 * (2026-09-11 mobile redesign): someone who chose the browser over the app
 * saw a 50px store pitch on every screen, on every visit, for as long as they
 * kept not tapping it. The store links live in the More sheet and in
 * Settings > Other Products instead. `SmartBanner` stays exported for a
 * future placement that earns it (a result-card footer, say).
 */
export default function MobileAppPromo() {
  const { eligible, platform } = useIsMobilePromoEligible()
  const [sheetVisible, setSheetVisible] = useState(false)

  // The one-time sheet waits for a moment of value — the visitor's FIRST
  // completed translation (the translator fires `baba:usage-updated` after
  // every run) — instead of interrupting them on arrival. Blind design review
  // flagged the on-load version as blocking the task before it could start.
  useEffect(() => {
    if (!eligible || !platform) return
    if (readLocal(SHEET_SEEN_KEY) === '1') return

    let timer: number | null = null
    const onFirstTranslation = () => {
      window.removeEventListener('baba:usage-updated', onFirstTranslation)
      if (isSheetExemptRoute(window.location.pathname)) return
      if (readLocal(SHEET_SEEN_KEY) === '1') return
      timer = window.setTimeout(() => {
        writeLocal(SHEET_SEEN_KEY, '1')
        setSheetVisible(true)
      }, SHEET_DELAY_MS)
    }
    window.addEventListener('baba:usage-updated', onFirstTranslation)
    return () => {
      window.removeEventListener('baba:usage-updated', onFirstTranslation)
      if (timer !== null) window.clearTimeout(timer)
    }
  }, [eligible, platform])

  if (!eligible || !platform || !sheetVisible) return null

  return <DownloadSheet platform={platform} onClose={() => setSheetVisible(false)} />
}
